import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EyeClosedIcon,
  EyeOpenIcon,
} from '@radix-ui/react-icons';
import { TrashIcon } from '@heroicons/react/24/solid';
import {
  CircleHalfIcon,
  CloudFogIcon,
  CloudLightningIcon,
  CloudRainIcon,
  SnowflakeIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { Toolpad } from '../Toolpad';
import { PropertyInput } from '../../ui/PropertyInput';
import { useUIStore, useEditorStore } from '../../../stores';
import { useSceneEffectsSync } from '../../../hooks/useSceneEffectsSync';
import { useTranslation, type MessageKey } from '../../../i18n';
import {
  SCENE_EFFECT_PARAM_UI,
  mergeEffectParams,
  type SceneEffectInstance,
  type SceneEffectParams,
  type SceneEffectType,
} from '../../../types/sceneEffects';

const EFFECT_META: {
  id: SceneEffectType;
  titleKey: MessageKey;
  icon: Icon;
}[] = [
  { id: 'fog', titleKey: 'toolbar.tools.fog', icon: CloudFogIcon },
  { id: 'rain', titleKey: 'toolbar.tools.rain', icon: CloudRainIcon },
  { id: 'snow', titleKey: 'toolbar.tools.snow', icon: SnowflakeIcon },
  { id: 'lightning', titleKey: 'toolbar.tools.lightning', icon: CloudLightningIcon },
  { id: 'particles', titleKey: 'toolbar.tools.particles', icon: SparkleIcon },
  { id: 'vignette', titleKey: 'toolbar.tools.vignette', icon: CircleHalfIcon },
];

const PARAM_LABEL_KEYS: Record<keyof SceneEffectParams, MessageKey> = {
  intensity: 'toolpads.sceneEffects.params.intensity',
  speed: 'toolpads.sceneEffects.params.speed',
  particleSize: 'toolpads.sceneEffects.params.particleSize',
  color: 'toolpads.sceneEffects.params.color',
};

const PATCH_DEBOUNCE_MS = 180;

export function SceneEffectList() {
  const { t } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const { upsertSceneEffect } = useEditorStore();
  const { sceneEffects, toggleEffect, patchEffect, deleteEffect, clearEffects } =
    useSceneEffectsSync();
  const isOpen = toolpads.sceneEffectList.isOpen;

  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const patchTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const pendingPatchesRef = useRef<Map<number, SceneEffectParams>>(new Map());

  const sortedEffects = useMemo(
    () =>
      [...sceneEffects].sort(
        (a, b) => a.sorter - b.sorter || a.effectType.localeCompare(b.effectType),
      ),
    [sceneEffects],
  );

  const enabledCount = useMemo(
    () => sortedEffects.filter((effect) => effect.isEnabled).length,
    [sortedEffects],
  );

  const flushPatch = useCallback(
    async (effectId: number) => {
      const patch = pendingPatchesRef.current.get(effectId);
      pendingPatchesRef.current.delete(effectId);
      if (!patch || Object.keys(patch).length === 0) return;
      try {
        await patchEffect(effectId, patch);
      } catch (error) {
        console.error('[SceneEffectList] Failed to patch effect properties:', error);
      }
    },
    [patchEffect],
  );

  const schedulePatch = useCallback(
    (effectId: number, patch: SceneEffectParams) => {
      const existing = pendingPatchesRef.current.get(effectId) ?? {};
      pendingPatchesRef.current.set(effectId, { ...existing, ...patch });

      const prevTimer = patchTimersRef.current.get(effectId);
      if (prevTimer) clearTimeout(prevTimer);

      const timer = setTimeout(() => {
        patchTimersRef.current.delete(effectId);
        void flushPatch(effectId);
      }, PATCH_DEBOUNCE_MS);
      patchTimersRef.current.set(effectId, timer);
    },
    [flushPatch],
  );

  const updateLocalProperties = useCallback(
    (effect: SceneEffectInstance, patch: SceneEffectParams) => {
      upsertSceneEffect({
        ...effect,
        properties: mergeEffectParams(effect.effectType, {
          ...effect.properties,
          ...patch,
        }),
      });
      schedulePatch(effect.id, patch);
    },
    [upsertSceneEffect, schedulePatch],
  );

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getMeta = (effectType: SceneEffectType) =>
    EFFECT_META.find((item) => item.id === effectType);

  return (
    <Toolpad
      title={t('toolpads.sceneEffects.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('sceneEffectList')}
    >
      <div className="effect-list">
        {sortedEffects.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-3 gap-2 h-full">
            <p className="text-xs text-theme-muted text-center">
              {t('toolpads.sceneEffects.empty')}
            </p>
          </div>
        ) : (
          <>
            <div className="effect-list-toolbar">
              <span className="effect-list-count">
                {t('toolpads.sceneEffects.activeCount', { count: enabledCount })}
              </span>
              {enabledCount > 0 && (
                <button
                  type="button"
                  className="effect-list-clear"
                  onClick={() => void clearEffects()}
                >
                  {t('toolpads.sceneEffects.clearAll')}
                </button>
              )}
            </div>

            {sortedEffects.map((effect) => {
              const meta = getMeta(effect.effectType);
              if (!meta) return null;

              const IconComponent = meta.icon;
              const isExpanded = expandedIds.has(effect.id);
              const params = mergeEffectParams(effect.effectType, effect.properties);
              const paramUi = SCENE_EFFECT_PARAM_UI[effect.effectType];

              return (
                <div
                  key={effect.id}
                  className={[
                    'effect-list-item-block',
                    effect.isEnabled ? 'is-enabled' : 'is-disabled',
                  ].join(' ')}
                >
                  <div
                    className={[
                      'effect-list-item flex',
                      effect.isEnabled ? '' : 'is-hidden',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <button
                      type="button"
                      className="effect-list-expand"
                      aria-label={
                        isExpanded
                          ? t('toolpads.sceneEffects.collapse')
                          : t('toolpads.sceneEffects.expand')
                      }
                      onClick={() => toggleExpanded(effect.id)}
                    >
                      {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    </button>

                    <IconComponent
                      aria-hidden="true"
                      className="effect-type-icon"
                      weight={effect.isEnabled ? 'fill' : 'regular'}
                    />

                    <span className="effect-name">{t(meta.titleKey)}</span>

                    <span className="effect-actions">
                      <button
                        type="button"
                        className={`cursor-pointer ${
                          effect.isEnabled ? 'is-object-visible' : 'is-object-hidden'
                        }`}
                        title={
                          effect.isEnabled
                            ? t('toolpads.sceneEffects.disable')
                            : t('toolpads.sceneEffects.enable')
                        }
                        aria-label={
                          effect.isEnabled
                            ? t('toolpads.sceneEffects.disable')
                            : t('toolpads.sceneEffects.enable')
                        }
                        onClick={() => void toggleEffect(effect.effectType)}
                      >
                        {effect.isEnabled ? (
                          <EyeOpenIcon className="w-3.5 h-3.5" />
                        ) : (
                          <EyeClosedIcon className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        type="button"
                        className="cursor-pointer object-delete"
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                        onClick={() => void deleteEffect(effect.id)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="effect-params">
                      {paramUi.map((param) => {
                        if (param.kind === 'color') {
                          const colorValue = params.color ?? '#ffffff';
                          return (
                            <div key={param.key} className="property-row-full">
                              <label className="label">
                                {t(PARAM_LABEL_KEYS.color)}
                              </label>
                              <div className="effect-color-row">
                                <input
                                  type="color"
                                  value={colorValue}
                                  className="color-style-input"
                                  aria-label={t(PARAM_LABEL_KEYS.color)}
                                  onChange={(e) =>
                                    updateLocalProperties(effect, { color: e.target.value })
                                  }
                                />
                                <span className="effect-color-value">{colorValue}</span>
                              </div>
                            </div>
                          );
                        }

                        const value = params[param.key] ?? 0;
                        return (
                          <div key={param.key} className="property-row-full">
                            <label className="label">{t(PARAM_LABEL_KEYS[param.key])}</label>
                            <div className="property-input-with-slider">
                              <input
                                type="range"
                                min={param.min}
                                max={param.max}
                                step={param.step}
                                value={value}
                                aria-label={t(PARAM_LABEL_KEYS[param.key])}
                                onChange={(e) =>
                                  updateLocalProperties(effect, {
                                    [param.key]: Number.parseFloat(e.target.value),
                                  })
                                }
                              />
                              <PropertyInput
                                className="property-input-inline"
                                value={value}
                                min={param.min}
                                max={param.max}
                                step={param.step}
                                onChange={(next) =>
                                  updateLocalProperties(effect, { [param.key]: next })
                                }
                                aria-label={t(PARAM_LABEL_KEYS[param.key])}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </Toolpad>
  );
}
